import { ExtendedPropertiesType, MutableExtendedPropertiesType, MutableTagsType, TagsType } from "./types";

/**
 * Pomocná funkce, která z elementu vytáhne extended properties.
 *
 * @param element
 * @returns
 */
export const getExtendedPropertiesFromElement = (element: HTMLElement): ExtendedPropertiesType =>
    element
        .dataset
        .extendedproperties
        ?.split(";")
        ?.reduce((extendedProperties, current) => {
            const [name, value = ""] = current.split("=");
            extendedProperties[name] = value;
            return extendedProperties;
        }, {} as MutableExtendedPropertiesType) || {};

/**
 * Pomocná funkce, která z elementu vytáhne tagy.
 *
 * @example
 * ```
 * const tagString = '["group-box--card", "detail-frame--transparent"]'
 * getTagsFromElement(tagString); // {"group-box--card": true, "detail-frame--transparent": true}
 * ```
 *
 * @param element
 * @returns
 */
export const getTagsFromElement = (element: HTMLElement): TagsType => {
    if (!element.dataset.tag) {
        return {};
    }
    const tags = JSON.parse(element.dataset.tag);
    if (Array.isArray(tags)) {
        return tags.reduce((tags, current) => {
            tags[current] = true;
            return tags;
        }, {} as MutableTagsType) || {};
    }

    return {};
};

/**
 * Na element nastaví dané dodatečné vlastnosti
 * @param extendedProperties dodatečné vlastnosti
 * @param element element, na který se vlastnosti přidají
 */
export const setExtendedPropertiesOnElement = (extendedProperties: ExtendedPropertiesType, element: HTMLElement) => {
    if (!extendedProperties || !element)
        return;

    const propNames = Object.keys(extendedProperties);
    propNames.forEach(name => {
        element.setAttribute(name, extendedProperties[name]);
    });
};

/** Sloučí extended properties do jedné množiny */
export const mergeExtendedProperties = (origProps: ExtendedPropertiesType | undefined, newProps: ExtendedPropertiesType): ExtendedPropertiesType | undefined => {
    if (!newProps) {
        return origProps;
    }
    if (!origProps) {
        return newProps;
    }
    return { ...origProps, ...newProps };
};

/**
 * Observer pro scrollovani
 * @param element
 * @param callback
 */
export function observeScroll(element: HTMLElement, callback: () => void): () => void {
    const listener = (e: Event) => {
        if ((e.target as HTMLElement).contains(element)) {
            callback();
        }
    };
    document.addEventListener("scroll", listener, { capture: true });
    return () => {
        document.removeEventListener("scroll", listener, { capture: true });
    };
}

/**
 * Observer pro změny velikosti elementu.
 * @param element
 * @param callback
 */
export function observeSize(element: HTMLElement, callback: () => void): () => void {
    const resizeObserver = new ResizeObserver(() => {
        callback();
    });
    resizeObserver.observe(element);
    return () => {
        resizeObserver.disconnect();
    };
}

/**
 * Observer pro změny pozice elementu.
 * @param element
 * @param callback
 */
export function observePosition(element: HTMLElement, callback: () => void): () => void {
    const positionObserver = document.createElement("div");
    Object.assign(positionObserver.style, {
        position: "fixed",
        pointerEvents: "none",
        width: "2px",
        height: "2px",
    });
    element.appendChild(positionObserver);

    const reposition = () => {
        const rect = positionObserver.getBoundingClientRect();
        Object.assign(positionObserver.style, {
            marginLeft: `${parseFloat(positionObserver.style.marginLeft || "0") - rect.left - 1}px`,
            marginTop: `${parseFloat(positionObserver.style.marginTop || "0") - rect.top - 1}px`,
        });
    };
    reposition();

    const intersectionObserver = new IntersectionObserver((entries) => {
        const visiblePixels = Math.round(entries[0].intersectionRatio * 4);
        if (visiblePixels !== 1) {
            reposition();
            callback();
        }
    }, {
        threshold: [0.125, 0.375, 0.625, 0.875],
    });
    intersectionObserver.observe(positionObserver);

    return () => {
        intersectionObserver.disconnect();
        positionObserver.remove();
    };
}

/**
 * Hlídá změny v getClientBoundingRect a v případě změny zavolá callback.
 * @param element
 * @param callback
 */
export function observeBounds(element: HTMLElement, callback: () => void): () => void {
    const destroyScroll = observeScroll(element, callback);
    const destroySize = observeSize(element, callback);
    const destroyPosition = observePosition(element, callback);
    return () => {
        destroyScroll();
        destroySize();
        destroyPosition();
    };
}
